import React, { useCallback, useMemo, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { TaskEntry } from '../../../../components/TaskEntry'
import type { TaskEntryProps } from '../../../../components/TaskEntry'
import { useLazyRow } from '../../../../components/LazyRows'
import './DraggableTaskEntry.scss'

interface DraggableTaskEntryProps extends TaskEntryProps {
  /** Task id — used as the dnd-kit sortable identifier. */
  taskId: string
  /** Wahr, solange irgendein Zug läuft — schaltet die Übergänge scharf. */
  isDragActive?: boolean
}

/**
 * Eine Zeile auf *Demnächst* als **sortierbares** Element.
 *
 * Bis zum 22.09.2026 waren es die rohen Bausteine (`useDraggable` plus
 * `useDroppable`). Die melden nur, worüber der Zeiger steht — sie bewegen
 * nichts. Es ging beim Ziehen also nie ein Platz auf, in den man hätte
 * ablegen können. `useSortable` ist derselbe Baustein wie im Projekt
 * (`components/TaskList/SortableTaskEntry.tsx`); wie weit jede Zeile
 * ausweicht, rechnet die Strategie am `SortableContext` der Seite aus.
 *
 * **Die Quellzeile bleibt stehen** (Nutzer, 22.09.2026) — sie wird nur
 * unsichtbar. Der Platz, aus dem gezogen wurde, bleibt damit reserviert;
 * im Projekt klappt er stattdessen zu. Auf *Demnächst* wäre das auffälliger:
 * Ein Tag mit einer einzigen Aufgabe sähe mitten im Zug leer aus.
 *
 * Abgeschaltet, solange die Zeile bearbeitet wird (`mode === 'expanded'`) —
 * sonst kämpften Anfassdistanz und Textcursor gegeneinander.
 */
const DraggableTaskEntryComponent: React.FC<DraggableTaskEntryProps> = ({
  taskId,
  isDragActive,
  ...taskEntryProps
}) => {
  const isDisabled = taskEntryProps.mode === 'expanded' || taskEntryProps.mode === 'creating'

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: taskId,
    disabled: isDisabled,
    // Übergänge nur WÄHREND eines Zuges. Danach aus, damit ein Nachziehen der
    // Daten (LiveQuery, optimistische Überschreibung) nicht als zweite
    // Bewegung sichtbar wird.
    transition: isDragActive ? { duration: 300, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' } : null,
    animateLayoutChanges: () => false,
  })

  const style = useMemo<React.CSSProperties>(
    () =>
      isDisabled
        ? { position: 'relative' }
        : {
            transform: CSS.Transform.toString(transform),
            transition: transition ?? undefined,
            // Die gezogene Zeile ist unsichtbar — das DragOverlay zeigt sie.
            opacity: isDragging ? 0 : undefined,
            zIndex: isDragging ? 10 : undefined,
            position: 'relative',
          },
    [isDisabled, transform, transition, isDragging],
  )

  /*
    Lazy Rendering (#303) wie im Projekt (`SortableTaskEntry`): weit draußen nur die Hülle. Anders als dort hält hier
    „ausgewählt“ die Zeile fest: Demnächst gibt `focused` nie weiter (`DayGroup`), die Auswahl IST der Fokus — sonst
    stand die Markierung nach einem Pfeil erst da, wenn die Zeile ins Bild gerollt war (gemessen 10.10.2026: P95 511 ms).
  */
  const rowRef = useRef<HTMLDivElement | null>(null)
  const ref = useCallback((el: HTMLDivElement | null) => {
    rowRef.current = el
    setNodeRef(el)
  }, [setNodeRef])
  const pinned = !!taskEntryProps.selected || !!taskEntryProps.focused || isDisabled || isDragging || !!taskEntryProps.initialExpanded || taskEntryProps.menuShortcut?.taskId === taskId
  const { built, placeholderHeight } = useLazyRow(taskId, rowRef, pinned)

  return (
    <div
      ref={ref}
      style={style}
      className="draggable-task-entry"
      {...attributes}
      /*
        Aufgeklappt ist das Ziehen aus, und dnd-kit meldet dann `aria-disabled` — am Kasten, der die ganze offene Karte
        trägt. Screenreader lasen damit jeden Button darin als deaktiviert vor, und Playwright klickte keinen davon
        (gemessen 10.10.2026: Belohnung in Demnächst). Die Karte ist bedienbar; nur das Ziehen ruht.
      */
      aria-disabled={isDisabled ? undefined : attributes['aria-disabled']}
      {...listeners}
    >
      {built
        ? <TaskEntry {...taskEntryProps} />
        : <div className="draggable-task-entry__placeholder" style={{ '--lazy-row-height': `${placeholderHeight}px` } as React.CSSProperties} />}
    </div>
  )
}

/*
  Memoisiert wie `SortableTaskEntry` (#303): Rendert ein Tag neu, bleiben seine Zeilen stehen, solange ihre Props
  gleich sind. Gemessen 10.10.2026: Ohne das renderte jeder Pfeil auf Demnächst alle geladenen Zeilen neu.
*/
export const DraggableTaskEntry = React.memo(DraggableTaskEntryComponent)
